import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { CategoryItem } from 'src/app/core/_models/category-item';
import { NavigationItem } from 'src/app/core/_models/navigation-item';
import { CODE_PAGE_QUAN_HE_CO_DONG } from 'src/app/core/_models/predefined-code-list';
import { CategoryService } from 'src/app/core/_services/category.service';
import { NavigationService } from 'src/app/core/_services/navigation.service';
import { SiteLanguageService } from 'src/app/core/_services/site-language.service';
import { StateService } from 'src/app/core/_services/state.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  @Output() showSearch = new EventEmitter<boolean>();
  @ViewChild('search') searchBoxFullScreen!: ElementRef<HTMLInputElement>;
  @ViewChild('mobileSearch') mobileSearchBox!: ElementRef<HTMLInputElement>;

  currentLanguage = 'vi';

  languageList = [
    { code: 'en', label: 'English' },
    { code: 'vi', label: 'Việt Nam' },
  ];

  expandMenuId = -1;
  isOpenMenu = false;
  isShowSearch = false;
  isShowSearchMobile = false;
  advisoryNav?: NavigationItem;
  portfolioNav?: NavigationItem;
  navAboutUs?: {
    item: NavigationItem;
    children: {
      item: NavigationItem;
      children?: {
        item: NavigationItem;
      }[];
    }[];
  };

  knowledgeNav?: {
    item: NavigationItem;
    children: {
      item: NavigationItem;
      children?: {
        item: NavigationItem;
      }[];
    }[];
  };

  investmentFundsNav?: {
    item: NavigationItem;
    children: {
      item: NavigationItem;
      children?: {
        item: NavigationItem;
      }[];
    }[];
  };

  // InformationDisclosureAtHomePage
  iDAtHomePage: (NavigationItem | CategoryItem)[] = [];

  // For About Us
  shareholderRelationsNav?: NavigationItem;
  catInformationDisclosure?: CategoryItem;

  searchTxt = ``;
  constructor(
    private _router: Router,
    private _cateService: CategoryService,
    private _translate: TranslateService,
    private _stateService: StateService,
    private _navService: NavigationService,
    private _siteLangService: SiteLanguageService,
    private _cdr: ChangeDetectorRef,
  ) {
    this._stateService.currentLanguage$.subscribe({
      next: (currentLang: string) => {
        this.currentLanguage = currentLang;
        this._cdr.markForCheck();
      },
    });
  }

  ngOnInit(): void {
    combineLatest([this._stateService.navTree$, this._stateService.categories$]).subscribe(() => {
      this.navAboutUs = this._navService.getAboutUsNav();
      this.advisoryNav = this._navService.getAdvisoryNav();
      this.portfolioNav = this._navService.getPortfolioNav();
      this.knowledgeNav = this._navService.getKnowledgeNav();
      this.investmentFundsNav = this._navService.getInvestmentFundsNav();

      // For Information Disclosure At Home Page
      this.shareholderRelationsNav = this._navService.getAboutUsShareholderRelationsNav();
      this.catInformationDisclosure = this._cateService.getCatCongBoThongTin();
      this.iDAtHomePage = [];

      if (this.investmentFundsNav && this.investmentFundsNav.children) {
        this.investmentFundsNav.children.forEach(
          (item: {
            item: NavigationItem;
            children?: {
              item: NavigationItem;
            }[];
          }) => {
            this.iDAtHomePage.push(item.item);
          },
        );
      }
      if (this.catInformationDisclosure) {
        const cateInfo = Object.assign({
          ...this.catInformationDisclosure,
          name: 'Công ty QLQ Rồng Việt',
          nameEn: 'Rong Viet Asset Management',
        });
        this.iDAtHomePage.unshift(cateInfo);
      }

      this._cdr.markForCheck();
    });
  }

  // trackBy helpers for *ngFor - avoids Angular re-rendering/re-diffing every
  // dropdown item on each change detection pass (reduces main-thread work).
  trackByLinkUrl(_index: number, entry: { item: NavigationItem; children?: unknown }): string {
    return entry?.item?.linkUrl ?? _index.toString();
  }

  trackByHomePageItem(_index: number, entry: NavigationItem | CategoryItem): string {
    return (entry as NavigationItem)?.linkUrl ?? _index.toString();
  }

  open(): void {
    this.isOpenMenu = true;
  }

  toggleMenu(): void {
    this.isOpenMenu = !this.isOpenMenu;
  }

  close(isClose: boolean): void {
    this.isOpenMenu = !isClose;
  }

  openSearch() {
    this.isShowSearch = true;
    setTimeout(() => {
      this.searchBoxFullScreen?.nativeElement.focus();
    }, 200);
  }

  openSeachMobile() {
    this.isShowSearchMobile = !this.isShowSearchMobile;
    this.showSearch.emit(this.isShowSearchMobile);
    if (this.isShowSearchMobile) {
      setTimeout(() => {
        this.mobileSearchBox?.nativeElement.focus();
      }, 0);
    }
  }

  closeSearch() {
    this.isShowSearch = false;
    this.showSearch.emit(false);
    this.enterSearch(this.searchTxt);
  }

  closeSearchMobile() {
    this.isShowSearchMobile = false;
    this.showSearch.emit(false);
    this.enterSearch(this.searchTxt);
  }

  enterSearch(value: string) {
    this.searchTxt = value;
    if (this.searchTxt) {
      this._router.navigate(['/' + this.currentLanguage + '/' + (this.currentLanguage === 'vi' ? 'tim-kiem' : 'search')], {
        queryParams: {
          keyword: this.searchTxt,
        },
      });
    } else {
      this.isShowSearch = false;
      this.isShowSearchMobile = false;
      this._router.navigate(['/' + this.currentLanguage + '/' + (this.currentLanguage === 'vi' ? 'tim-kiem' : 'search')], {
        queryParams: {
          keyword: '',
        },
      });
    }
  }

  async changeSiteLanguage(localeCode: string): Promise<void> {
    if (this.currentLanguage === localeCode) return;
    const selectedLanguage = this.languageList.find((language) => language.code === localeCode)?.label.toString();
    if (selectedLanguage) {
      this._stateService.currentLanguage$.next(localeCode);
      this._translate.use(localeCode);
      await this._siteLangService.createTree();
      const currentPage = this._stateService.currentPage$.getValue();
      this._stateService.changeSiteLanguage$.next(currentPage);
    }
  }

  checkQuanHeCoDongAboutUs(item: NavigationItem | CategoryItem) {
    if ((item as CategoryItem).navigationCode) {
      if ((item as CategoryItem).navigationCode === CODE_PAGE_QUAN_HE_CO_DONG) {
        return true;
      }
    }
    return false;
  }

  checkShowOnMenu(item: NavigationItem | CategoryItem) {
    if ((item as NavigationItem).status && (item as NavigationItem).showMenu) {
      return true;
    }
    return false;
  }

  openfDragon() {
    window.open('https://fdragon.vdam.com.vn/', '_blank');
  }
}
